/**
 * The last generation run this browser started, remembered across a reload / nav-away so the
 * applications surface (and a retry from the review surface) can re-open it. Scoped per user;
 * sessionStorage (not local) so it dies with the tab, not with the machine. Guarded for
 * private-mode / SSR / test environments where the API is absent.
 *
 * Why this exists (the same defect the SearchRun rail had — see `readLastSearchStream` in
 * `career-search.ts`): the GenerationRun rail is fed ONLY by the push plane, opened during
 * `generate()`. On a cold load — a hard reload while the letter is being written, or a
 * re-login after the token expired MID-run — that subscription is gone, so the rail comes back
 * EMPTY and a run that failed while the surface was away leaves no trace at all. Re-subscribing
 * on mount lets the push plane REPLAY both streams and fold the run — its terminal state, its
 * stage and its error — right back into the rail. Two streams, because a generation writes to
 * two: the GenerationRun (the rail) and the Application (the content).
 *
 * The durable home for this is a server-side "recent generations" read; until that contract
 * exists, the client remembers the streams it opened.
 */

/** What `POST /api/applications/generate` hands back that is worth remembering. */
export interface LastGeneration {
  streamId: string
  applicationStreamId: string
}

const key = (userId: string): string => `career:last-generation:${userId}`

export function readLastGeneration(userId: string | null): LastGeneration | null {
  if (!userId || typeof sessionStorage === "undefined") return null
  try {
    const raw = sessionStorage.getItem(key(userId))
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== "object") return null
    const { streamId, applicationStreamId } = parsed as Record<string, unknown>
    if (typeof streamId !== "string" || typeof applicationStreamId !== "string") return null
    return { streamId, applicationStreamId }
  } catch {
    // absent / private-mode / corrupt JSON — resuming is a convenience, never load-bearing.
    return null
  }
}

export function writeLastGeneration(userId: string | null, run: LastGeneration): void {
  if (!userId || typeof sessionStorage === "undefined") return
  try {
    sessionStorage.setItem(key(userId), JSON.stringify(run))
  } catch {
    // private-mode / quota — remembering the run is a convenience, never load-bearing.
  }
}
